/* Rhody Reviews api/v1 -- notifications domain. PURE EXTRACTION from lib/api.ts (gate-prep split); no behavior change. */
import { request } from './_http';

/* Owner new-review notification preferences
   (GET/PUT /api/v1/notification-preferences). Drives Brad's Settings ->
   Notifications tab. Email is the default channel (instant for 1-2 star,
   digest for positives); SMS is opt-in, negatives only; the in-app bell is
   always on regardless of these flags. */
export type NotificationTriggerScope =
  | 'all_reviews'
  | 'negative_only'
  | 'milestones_too';
export type NotificationPositivesMode = 'instant' | 'digest' | 'off';

export interface NotificationPreferences {
  email_enabled: boolean;
  sms_enabled: boolean;
  push_enabled: boolean;
  trigger_scope: NotificationTriggerScope;
  positives_mode: NotificationPositivesMode;
  digest_frequency: string;
  owner_email: string | null;
  owner_phone: string | null;
  /* Where alerts actually land, after falling back to the billing contact.
     Present on GET only (read-only, server-computed). */
  effective_owner_email?: string | null;
  effective_owner_phone?: string | null;
}

export interface NotificationPreferencesResponse {
  client_id: string;
  /* True when no prefs row exists yet and `preferences` is the defaults. */
  defaulted?: boolean;
  preferences: NotificationPreferences;
}

/* -------------------------------------------------------------------
   Notifications feed (the bell + Dashboard "Needs attention" alerts) --
   GET /api/v1/notifications?unread=&limit=. Matches list_notifications()
   in engine/api/v1/app.py. Most-recent first; unread_count drives the bell
   badge + the alert panel. An empty feed comes back as [] + 0 (never
   invented). kind maps to the AlertRow visual (warn/info/win).
   ------------------------------------------------------------------- */
export type NotificationKind = 'warn' | 'info' | 'win';

export interface Notification {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string;
  /* In-app route the alert deep-links to (e.g. a screen view). May be null. */
  link_view: string | null;
  read_at: string | null;
  created_at: string;
}

export interface NotificationsResponse {
  client_id: string;
  count: number;
  unread_count: number;
  notifications: Notification[];
}

/* Notifications -- mark read (POST /notifications/<id>/read, /read-all). */
export interface NotificationReadResult {
  client_id: string;
  id: string;
  read_at: string | null;
  unread_count: number;
}
export interface NotificationsReadAllResult {
  client_id: string;
  marked_read: number;
  unread_count: number;
}

export const notificationsApi = {
  /* The client's notification feed for the bell + Dashboard "Needs
     attention" panel. unread=true filters to unread rows; limit caps the
     feed (server default 50, max 200). Always returns unread_count off one
     round-trip. Empty feed -> [] + 0 (never invented). */
  getNotifications(
    params: { unread?: boolean; limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<NotificationsResponse> {
    return request<NotificationsResponse>('/api/v1/notifications', {
      query: { unread: params.unread, limit: params.limit },
      signal,
    });
  },

  /* Owner new-review notification preferences. GET returns the saved row or
     the documented defaults (with effective_owner_* fallbacks). update sends
     any subset of editable fields. */
  getNotificationPreferences(
    signal?: AbortSignal,
  ): Promise<NotificationPreferencesResponse> {
    return request<NotificationPreferencesResponse>(
      '/api/v1/notification-preferences',
      { signal },
    );
  },

  updateNotificationPreferences(
    patch: Partial<
      Pick<
        NotificationPreferences,
        | 'email_enabled'
        | 'sms_enabled'
        | 'push_enabled'
        | 'trigger_scope'
        | 'positives_mode'
        | 'digest_frequency'
        | 'owner_email'
        | 'owner_phone'
      >
    >,
  ): Promise<NotificationPreferencesResponse> {
    return request<NotificationPreferencesResponse>(
      '/api/v1/notification-preferences',
      { method: 'PUT', body: patch },
    );
  },

  /* ----- Notifications -- mark read ----- */
  markNotificationRead(id: string): Promise<NotificationReadResult> {
    return request<NotificationReadResult>(
      `/api/v1/notifications/${encodeURIComponent(id)}/read`,
      { method: 'POST', body: {} },
    );
  },
  markAllNotificationsRead(): Promise<NotificationsReadAllResult> {
    return request<NotificationsReadAllResult>('/api/v1/notifications/read-all', {
      method: 'POST',
      body: {},
    });
  },
};
